import ProfileAvatar from '-/Profile/ProfileAvatar';
import { TabTrigger } from 'expo-router/ui';
import { type ComponentProps, type ReactNode, startTransition } from 'react';
import { Drawer as DrawerLayout } from 'react-native-drawer-layout';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import useCloseDrawerOnBackground from '@/hooks/useCloseDrawerOnBackground';
import useCurrentUser from '@/hooks/useCurrentUser';
import useHaptics from '@/hooks/useHaptics';
import useRootStore, {
  closeDrawer,
  navigateFromDrawer,
  openDrawer,
} from '@/stores/useRootStore';
import Box from '../../UI/Box';
import Icon from '../../Unistyles/Icon';
import Pressable, { style as containerStyle } from '../../Unistyles/Pressable';
import DrawerEntityList from './DrawerEntityList';

type TabConfig = {
  name: 'event' | 'chat';
  label: string;
  activeIcon: ComponentProps<typeof Icon>['name'];
  inactiveIcon: ComponentProps<typeof Icon>['name'];
  badge?: number;
};

const UnistylesDrawer = withUnistyles(DrawerLayout);

function NavTriggerItem({
  tab,
  isFocused,
  badge,
  onPress,
  hideLabel = false,
}: {
  tab: TabConfig;
  isFocused?: boolean;
  badge?: number;
  onPress?: (args: unknown) => void;
  hideLabel?: boolean;
}) {
  const _onPress = useHaptics((args) => {
    closeDrawer();
    onPress?.(args);
  });

  return (
    <Pressable
      onPress={_onPress}
      styleOverride={({ pressed }) => [
        containerStyle.container(undefined),
        style.tabTrigger(isFocused),
        pressed && containerStyle.active,
      ]}
    >
      <Box
        direction="horizontal"
        alignItems="center"
        justifyContent="center"
        space="md"
      >
        <Box style={style.iconContainer}>
          <Icon
            name={isFocused ? tab.activeIcon : tab.inactiveIcon}
            size="3xl"
          />
          {badge ? (
            <Box style={style.badge}>
              <Text style={style.badgeText}>{badge}</Text>
            </Box>
          ) : null}
        </Box>
        {!hideLabel ? <Text style={style.tabLabel}>{tab.label}</Text> : null}
      </Box>
    </Pressable>
  );
}

export default function Drawer({
  children,
  tabs,
}: {
  children: ReactNode;
  tabs: TabConfig[];
}) {
  const user = useCurrentUser();
  const isDrawerOpen = useRootStore((state) => state.isDrawerOpen);

  useCloseDrawerOnBackground();

  return (
    <UnistylesDrawer
      uniProps={(theme, rt) => {
        const isLarge = rt.screen.width > theme.breakpoints.md;
        return {
          overlayStyle: {
            display: isLarge ? 'none' : 'flex',
          },
          drawerStyle: {
            width: theme.size(64),
          },
          drawerType: isLarge ? 'permanent' : 'back',
        };
      }}
      open={isDrawerOpen}
      onOpen={() => {
        startTransition(openDrawer);
      }}
      onClose={() => {
        startTransition(closeDrawer);
      }}
      swipeEnabled={false}
      renderDrawerContent={() => {
        return (
          <Box style={style.drawerContainer}>
            <Box style={style.drawerContent}>
              <Box style={style.drawerTabList}>
                {tabs.map((tab) => (
                  <TabTrigger
                    key={tab.name}
                    name={tab.name}
                    href={`/${tab.name}`}
                    asChild
                  >
                    <NavTriggerItem tab={tab} badge={tab.badge} />
                  </TabTrigger>
                ))}
              </Box>
              <DrawerEntityList />
            </Box>
            <Box justifyContent="flex-end">
              <Pressable
                accessibilityLabel="Open profile"
                onPress={() => navigateFromDrawer('/profile')}
                style={style.profileTrigger}
                testID="OpenProfileButton"
              >
                <Box style={style.profileTriggerContent}>
                  <ProfileAvatar />
                  <Text>{user?.name}</Text>
                </Box>
                <Icon name="settings-outline" variant="typographySecondary" />
              </Pressable>
            </Box>
          </Box>
        );
      }}
    >
      {children}
    </UnistylesDrawer>
  );
}

const style = StyleSheet.create((theme, rt) => ({
  drawerContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRightWidth: theme.size(0.5),
    borderRightColor: theme.colors.borderMuted,
    paddingBottom: rt.insets.bottom,
  },
  drawerContent: {
    paddingTop: rt.insets.top + theme.space('lg'),
    flex: 1,
  },
  drawerTabList: {
    paddingHorizontal: theme.space('lg'),
    gap: theme.space('sm'),
  },
  tabTrigger: (isFocused?: boolean) => ({
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.xl,
    md: theme.borderRadius.xl,
    paddingVertical: theme.space('md'),
    paddingHorizontal: theme.space('lg'),
    backgroundColor: isFocused ? theme.colors.surfaceSecondary : 'transparent',
  }),
  tabLabel: {
    fontSize: theme.fontSize.xl,
    fontWeight: theme.fontWeight.medium,
  },
  iconContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: -theme.size(1.5),
    right: -theme.size(3),
    minWidth: theme.size(5),
    height: theme.size(5),
    paddingHorizontal: theme.size(1),
    borderRadius: theme.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.info,
  },
  badgeText: {
    color: theme.colors.white,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
  },
  profileTrigger: {
    width: '100%',
    marginTop: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.space('md'),
    padding: theme.space('xl'),
    borderTopWidth: theme.size(0.5),
    borderTopColor: theme.colors.borderMuted,
  },
  profileTriggerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.space('md'),
  },
}));
